<template>
  <Teleport to="body">
    <div v-if="visible" class="publicdata-tour" @keydown.tab="trapFocus">
      <div class="publicdata-shade" :style="shadeStyle" @click="close()"></div>
      <section ref="cardRef" class="publicdata-card" :class="{ 'is-below': placement.below, 'is-plain': !guide.showAiPath }" :style="placement.style"
        role="dialog" aria-labelledby="publicdata-tour-title" aria-describedby="publicdata-tour-desc">
        <header>
          <span class="publicdata-step"><BiosIcon name="mouse-pointer-click" />入口指引</span>
          <button type="button" class="publicdata-close" aria-label="关闭引导" @click="close()"><BiosIcon name="x" /></button>
        </header>
        <h2 id="publicdata-tour-title">{{ guide.title }}</h2>
        <p id="publicdata-tour-desc">{{ guide.description }}</p>
        <div v-if="guide.showAiPath" class="publicdata-path"><BiosIcon name="sparkles" /><b>AI 检索模式</b><span>用自然语言对话找数据</span></div>
        <label class="publicdata-preference"><input v-model="skipNext" type="checkbox">以后不再显示</label>
        <footer>
          <button type="button" class="publicdata-cancel" @click="close()">暂不进入</button>
          <button ref="goRef" type="button" class="publicdata-go" :disabled="going" @click="go">{{ guide.action }}</button>
        </footer>
      </section>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import BiosIcon from '@/components/BiosIcon.vue'
import { ENTRY_GUIDES, PUBLIC_DATA_GUIDE_EVENT } from '@/utils/publicDataGuide'

const router = useRouter()
const PAD = 7

const visible = ref(false)
const guide = ref(ENTRY_GUIDES['public-data'])
const skipNext = ref(false)
const going = ref(false)
const cardRef = ref(null)
const goRef = ref(null)
const rects = reactive({ target: null, viewport: { width: 0, height: 0 }, card: { width: 360, height: 288 } })
let target = null
let returnFocus = null

const readSkip = () => { try { return localStorage.getItem(guide.value.skipKey) === '1' } catch { return false } }
const findTarget = () => document.querySelector(`[data-nav-path="${guide.value.navPath}"]`)

// 一整块遮罩挖洞：原型的四块拼接在非整数缩放下会露出贯穿全屏的细缝。
const shadeStyle = computed(() => {
  const { width, height } = rects.viewport
  const box = rects.target
  if (!box) return {}
  const x = Math.max(0, box.left - PAD), y = Math.max(0, box.top - PAD)
  const right = Math.min(width, box.right + PAD), bottom = Math.min(height, box.bottom + PAD)
  const hole = [[x, y], [right, y], [right, bottom], [x, bottom], [x, y]]
  const points = [[0, 0], [width, 0], [width, height], [0, height], [0, 0], ...hole, [0, 0]]
  return { clipPath: `polygon(evenodd, ${points.map(([px, py]) => `${px}px ${py}px`).join(', ')})` }
})

// 卡片放在导航项右侧 20px；空间不够时放到导航项下方（与原型 placePublicTour 相同）。
const placement = computed(() => {
  const { width, height } = rects.viewport
  const box = rects.target
  const { width: cardWidth, height: cardHeight } = rects.card
  if (!box) return { below: false, style: { left: '50%', top: '50%', transform: 'translate(-50%, -50%)' } }
  const x = Math.max(0, box.left - PAD), y = Math.max(0, box.top - PAD), right = box.right + PAD, bottom = box.bottom + PAD
  let left = right + 20, top = Math.max(14, y - 12), below = false
  if (left + cardWidth > width - 14) left = Math.max(14, x - cardWidth - 20)
  if (width < 620 || left < 14) {
    left = Math.min(Math.max(12, x), Math.max(12, width - cardWidth - 12))
    top = Math.min(height - cardHeight - 12, bottom + 18)
    below = true
  }
  top = Math.max(12, Math.min(top, height - cardHeight - 12))
  return { below, style: { left: `${left}px`, top: `${top}px` } }
})

function measure() {
  rects.viewport = { width: window.innerWidth, height: window.innerHeight }
  rects.target = target?.getBoundingClientRect() ?? null
  if (cardRef.value) rects.card = { width: cardRef.value.offsetWidth || 360, height: cardRef.value.offsetHeight || 288 }
}

function close(restoreFocus = true) {
  if (!visible.value) return
  visible.value = false
  target?.classList.remove('is-publicdata-target')
  target?.removeEventListener('click', onTargetClick, true)
  window.removeEventListener('resize', measure)
  if (restoreFocus) returnFocus?.focus?.()
}

async function goToDestination() {
  const { route, focusAfter } = guide.value
  await router.push(route)
  if (!focusAfter) return
  await nextTick()
  setTimeout(() => document.querySelector(focusAfter)?.focus(), 300)
}

function go() {
  if (going.value) return
  going.value = true
  if (skipNext.value) { try { localStorage.setItem(guide.value.skipKey, '1') } catch { /* 浏览器不可写时本次仍可进入 */ } }
  close(false)
  goToDestination()
}

function onTargetClick(event) {
  event.preventDefault()
  event.stopImmediatePropagation()
  go()
}

async function open(event) {
  if (visible.value) return
  guide.value = ENTRY_GUIDES[event?.detail?.entry] ?? ENTRY_GUIDES['public-data']
  if (readSkip()) return goToDestination()
  target = findTarget()
  returnFocus = document.activeElement
  skipNext.value = false
  going.value = false
  target?.classList.add('is-publicdata-target')
  target?.addEventListener('click', onTargetClick, true)
  visible.value = true
  window.addEventListener('resize', measure)
  measure()
  await nextTick()
  measure()
  goRef.value?.focus()
}

function onKeydown(event) {
  if (visible.value && event.key === 'Escape') { event.preventDefault(); close() }
}

// Tab 只在高亮的导航项与卡片控件之间循环。
function trapFocus(event) {
  const controls = [target, ...(cardRef.value?.querySelectorAll('button, input') ?? [])].filter(Boolean)
  const index = controls.indexOf(document.activeElement)
  event.preventDefault()
  const next = (index + (event.shiftKey ? -1 : 1) + controls.length) % controls.length
  controls[next]?.focus()
}

onMounted(() => {
  window.addEventListener(PUBLIC_DATA_GUIDE_EVENT, open)
  document.addEventListener('keydown', onKeydown)
})
onBeforeUnmount(() => {
  close(false)
  window.removeEventListener(PUBLIC_DATA_GUIDE_EVENT, open)
  document.removeEventListener('keydown', onKeydown)
})
</script>

<style>
/* 高亮的主导航项（原型 .is-publicdata-target） */
.is-publicdata-target {
  position: relative;
  z-index: 2310;
  background: #fff !important;
  color: #0840e6 !important;
  font-weight: 600;
  box-shadow: 0 0 0 7px rgba(8, 64, 230, .28), 0 10px 28px rgba(8, 64, 230, .24);
  outline: 3px solid #fff;
  outline-offset: 4px;
}
</style>

<style scoped>
.publicdata-tour { position: fixed; inset: 0; z-index: 2300; font-family: var(--font-family-sans); }
.publicdata-shade { position: absolute; inset: 0; background: rgba(28, 39, 63, .52); backdrop-filter: blur(2px); }
.publicdata-card {
  position: absolute;
  box-sizing: border-box;
  width: 360px;
  padding: 18px;
  border: 1px solid #d6e1fb;
  border-radius: 14px;
  background: #fff;
  color: #17233d;
  font-size: 14px;
  line-height: 21px;
  box-shadow: 0 20px 56px rgba(17, 34, 70, .3);
}
.publicdata-card::before {
  content: '';
  position: absolute;
  top: 28px;
  left: -8px;
  width: 15px;
  height: 15px;
  box-sizing: border-box;
  background: #fff;
  border-left: 1px solid #d6e1fb;
  border-bottom: 1px solid #d6e1fb;
  transform: rotate(45deg);
}
.publicdata-card.is-below::before { display: none; }
header { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.publicdata-step { display: flex; align-items: center; gap: 8px; color: #0840e6; font-size: 12px; line-height: 21px; font-weight: 600; }
.publicdata-close { display: grid; place-items: center; width: 29px; height: 29px; padding: 0; border: 0; border-radius: 8px; background: transparent; color: #69778f; cursor: pointer; }
.publicdata-close:hover { background: #f8faff; }
h2 { margin: 12px 0 7px; font-size: 16px; line-height: 20px; font-weight: 600; }
p { margin: 0; color: #69778f; font-size: 12px; line-height: 20.4px; }
.is-plain h2 { margin: 12px 0 10px; line-height: 24px; }
.is-plain p { font-size: 14px; line-height: 24px; }
.is-plain .publicdata-preference { margin-top: 20px; }
.is-plain footer { margin-top: 20px; }
.publicdata-path { display: flex; align-items: center; gap: 9px; margin: 13px 0; padding: 9px 10px; border-radius: 9px; background: #eef4ff; color: #0840e6; font-size: 12px; line-height: 21px; }
.publicdata-path b { color: #1648bd; font-weight: 600; }
.publicdata-path span { color: #69778f; }
.publicdata-preference { display: flex; align-items: center; gap: 10px; color: #17233d; font-size: 12px; line-height: 21px; cursor: pointer; }
.publicdata-preference input { width: 15px; height: 15px; margin: 0; accent-color: #0840e6; cursor: pointer; }
footer { display: flex; justify-content: flex-end; gap: 10px; margin-top: 16px; }
footer button { min-height: 36px; padding: 8px 13px; border-radius: 9px; font: inherit; font-size: 12px; line-height: 21px; cursor: pointer; }
.publicdata-cancel { border: 1px solid #e3e9f4; background: #fff; color: #17233d; }
.publicdata-go { border: 1px solid #0840e6; background: #0840e6; color: #fff; }
.publicdata-go:disabled { cursor: wait; opacity: .7; }
button:focus-visible { outline: 2px solid #0840e6; outline-offset: 2px; }
</style>
